import React, { useState } from 'react'
import Avatar from '@mui/material/Avatar'
import Collapse from '@mui/material/Collapse'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined'
import ExitToAppSharp from '@mui/icons-material/ExitToAppSharp'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import LightModeOutlined from '@mui/icons-material/LightModeOutlined'
import Notifications from '@mui/icons-material/Notifications'
import SettingsBrightness from '@mui/icons-material/SettingsBrightness'
import { useNavigate } from 'react-router-dom'
import { ColorModePreference, useColorMode } from 'contexts/ColorModeContext'
import { useAuth } from 'hooks/auth/useAuth'

const THEME_OPTIONS: { value: ColorModePreference; label: string; icon: React.ReactNode }[] = [
  { value: 'light', label: 'Light', icon: <LightModeOutlined /> },
  { value: 'dark', label: 'Dark', icon: <DarkModeOutlined /> },
  { value: 'system', label: 'System', icon: <SettingsBrightness /> },
]

const nestedSx = {
  minHeight: 48,
  pl: 4,
  maxWidth: '100%',
  overflow: 'hidden',
} as const

const iconSx = {
  minWidth: 0,
  mr: 2,
  justifyContent: 'center',
  flexShrink: 0,
} as const

interface CoreAccountDrawerSectionProps {
  onNavigate: () => void
}

export const CoreAccountDrawerSection: React.FC<CoreAccountDrawerSectionProps> = ({
  onNavigate,
}) => {
  const navigate = useNavigate()
  const { userInfo, logout } = useAuth()
  const { preference, setPreference } = useColorMode()
  const [open, setOpen] = useState(false)
  const role = userInfo?.role
  const canSeeNotifications = role === 'admin' || role === 'babyAdmin'
  const displayName = [userInfo?.first_name, userInfo?.last_name].filter(Boolean).join(' ')

  return (
    <List sx={{ py: 0, width: '100%', maxWidth: '100%', overflowX: 'hidden' }} aria-label='Account'>
      <ListItem disablePadding sx={{ display: 'block', maxWidth: '100%' }}>
        <ListItemButton
          onClick={() => setOpen(current => !current)}
          aria-expanded={open}
          aria-controls='account-drawer-submenu'
          sx={{ minHeight: 48, px: 2, maxWidth: '100%', overflow: 'hidden' }}
        >
          <ListItemIcon sx={iconSx}>
            <Avatar sx={{ width: 32, height: 32 }} src={userInfo?.profilePicture || undefined}>
              {userInfo?.first_name?.[0]}
              {userInfo?.last_name?.[0]}
            </Avatar>
          </ListItemIcon>
          <ListItemText
            primary={displayName || userInfo?.email || 'Account'}
            primaryTypographyProps={{ noWrap: true }}
            sx={{ minWidth: 0 }}
          />
          {open ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
      </ListItem>
      <Collapse id='account-drawer-submenu' in={open} timeout='auto' unmountOnExit>
        <List component='div' disablePadding sx={{ maxWidth: '100%', overflowX: 'hidden' }}>
          {canSeeNotifications ? (
            <ListItem disablePadding sx={{ display: 'block', maxWidth: '100%' }}>
              <ListItemButton
                onClick={() => {
                  navigate('/notification-center')
                  onNavigate()
                }}
                sx={nestedSx}
              >
                <ListItemIcon sx={iconSx}>
                  <Notifications />
                </ListItemIcon>
                <ListItemText
                  primary='Notification Center'
                  primaryTypographyProps={{ noWrap: true }}
                  sx={{ minWidth: 0 }}
                />
              </ListItemButton>
            </ListItem>
          ) : null}
          {THEME_OPTIONS.map(option => (
            <ListItem key={option.value} disablePadding sx={{ display: 'block', maxWidth: '100%' }}>
              <ListItemButton
                selected={preference === option.value}
                onClick={() => setPreference(option.value)}
                sx={nestedSx}
              >
                <ListItemIcon sx={iconSx}>{option.icon}</ListItemIcon>
                <ListItemText
                  primary={option.label}
                  primaryTypographyProps={{ noWrap: true }}
                  sx={{ minWidth: 0 }}
                />
              </ListItemButton>
            </ListItem>
          ))}
          <ListItem disablePadding sx={{ display: 'block', maxWidth: '100%' }}>
            <ListItemButton
              onClick={async () => {
                await logout()
                onNavigate()
              }}
              sx={{ ...nestedSx, color: 'error.main' }}
            >
              <ListItemIcon sx={{ ...iconSx, color: 'error.main' }}>
                <ExitToAppSharp />
              </ListItemIcon>
              <ListItemText
                primary='Logout'
                primaryTypographyProps={{ noWrap: true }}
                sx={{ minWidth: 0 }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Collapse>
    </List>
  )
}
